import React, { useMemo, type ComponentType } from 'react';
import noop from 'lodash/noop';
import { graphql, useFragment } from 'react-relay';
import { isLoaderErrorAttributes } from '@atlassian/jira-errors-handling/src/utils/is-loader-error-attributes.tsx';
import { GroupByDropdown as GroupByDropdownItem } from '@atlassian/jira-issue-table-group-by-dropdown/src/index.tsx';
import type { main_issueNavigator_Header_filter$key as FilterFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_Header_filter.graphql';
import type {
	main_issueNavigator_Header_issueResults$key as IssueResultsFragment,
	main_issueNavigator_Header_issueResults$data as IssueResultsData,
} from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_Header_issueResults.graphql';
import type { main_issueNavigator_Header_jiraViewResult$key as JiraViewResultFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_Header_jiraViewResult.graphql';
import type { main_issueNavigator_Header_viewResult$key as ViewResultFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_Header_viewResult.graphql';
import UFOLabel from '@atlassian/jira-ufo-label/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { isRefactorNinToViewSchemaEnabled } from '@atlassian/jira-issue-navigator-rollout/src/is-refactor-nin-to-view-schema-enabled.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { HideDoneItemsToggle } from '@atlassian/jira-issue-table-hide-done-toggle/src/index.tsx';
import { useIssueTableSelectedIssuesActions } from '@atlassian/jira-native-issue-table/src/controllers/selected-issues/index.tsx';
import { SaveResetViewSettingsButton } from '@atlassian/jira-issue-navigator-save-reset-view-settings-button/src/ui/SaveResetViewSettingsButton.tsx';
import { PACKAGE_NAME, TEAM_NAME, isJiraSpreadsheetView } from '../../common/constants.tsx';
import type { CustomHeaderProps } from '../../common/types.tsx';
import { HierarchyToggleItem } from '../../common/ui/hierarchy-toggle/index.tsx';
import { withReportErrors } from '../../common/ui/with-report-errors/index.tsx';
import { useSelectedViewState } from '../../controllers/selected-view-state/index.tsx';
import { useActiveJql, useResolvedJql } from '../../services/active-jql/index.tsx';
import { useFilterQuery } from '../../services/filter-query/index.tsx';
import { useIssueSearchActions } from '../../services/issue-search/selectors.tsx';

export type HeaderProps = {
	issueResults: IssueResultsFragment | null;
	CustomHeader: ComponentType<CustomHeaderProps>;
	filter: FilterFragment | null;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	HeaderSkeletonImage: ComponentType<Record<any, any>>;
	viewResult: ViewResultFragment | null;
	jiraViewResult: JiraViewResultFragment | null;
	isGroupingSupported?: boolean;
};

const getLastPage = (
	pageCursors: IssueResultsData['pageCursors'] | undefined,
): number | null | undefined => {
	if (pageCursors?.last) {
		return pageCursors.last.pageNumber;
	}

	if (pageCursors?.around?.length) {
		return pageCursors.around[pageCursors.around.length - 1]?.pageNumber;
	}

	return undefined;
};

const Header = ({
	CustomHeader,
	filter,
	issueResults,
	HeaderSkeletonImage,
	viewResult,
	jiraViewResult,
	isGroupingSupported,
}: HeaderProps) => {
	const issueResultsData = useFragment<IssueResultsFragment>(
		graphql`
			fragment main_issueNavigator_Header_issueResults on JiraIssueConnection {
				issueNavigatorPageInfo {
					firstIssuePosition
				}
				pageCursors(maxCursors: 7) {
					around {
						pageNumber
					}
					last {
						pageNumber
					}
				}
				totalIssueSearchResultCount
			}
		`,
		issueResults,
	);
	// Compute page data for our search results which is needed when requesting items for the legacy meatball menu
	const offset = (issueResultsData?.issueNavigatorPageInfo?.firstIssuePosition ?? 1) - 1; // offset is 0 based so subtract 1
	const pages = getLastPage(issueResultsData?.pageCursors) ?? 0;
	const total = issueResultsData?.totalIssueSearchResultCount ?? 0;

	const searchResultPageData = useMemo(
		() => ({
			offset,
			pages,
			total,
		}),
		[offset, pages, total],
	);

	const { filterId } = useActiveJql();
	const jql = useResolvedJql();
	const { filterQueryIsFetching } = useFilterQuery();

	const filterData = useFragment(
		graphql`
			fragment main_issueNavigator_Header_filter on JiraFilter {
				filterId
				name
				isFavourite
				jql
			}
		`,
		filter,
	);
	const viewResultData = useFragment<ViewResultFragment>(
		graphql`
			fragment main_issueNavigator_Header_viewResult on JiraIssueSearchView
			@argumentDefinitions(
				isSavedListViewEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-enabled.relayprovider"
				}
			) {
				...hierarchyToggle_issueNavigator_HierarchyToggleItem
				...src_issueTableHideDoneToggle_HideDoneItemsToggle
				...SaveResetViewSettingsButton @include(if: $isSavedListViewEnabled)
				viewConfigSettings(staticViewInput: $staticViewInput) {
					hideDone
				}
			}
		`,
		viewResult,
	);
	const jiraViewData = useFragment<JiraViewResultFragment>(
		graphql`
			fragment main_issueNavigator_Header_jiraViewResult on JiraView
			@argumentDefinitions(
				isSavedListViewEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-enabled.relayprovider"
				}
			) {
				__typename
				... on JiraSpreadsheetView {
					...hierarchyToggle_issueNavigator_HierarchyToggleItem
					...ui_issueTableGroupByDropdown_GroupByDropdown
					...src_issueTableHideDoneToggle_HideDoneItemsToggle
					...SaveResetViewSettingsButton @include(if: $isSavedListViewEnabled)
					viewSettings(
						groupBy: $groupBy
						staticViewInput: $staticViewInput
						issueSearchInput: $issueSearchInput
					) {
						hideDone
					}
				}
			}
		`,
		jiraViewResult,
	);

	const filterDetails = useMemo(
		() =>
			filterData
				? {
						filterId: filterData.filterId,
						name: filterData.name,
						jql: filterData.jql,
						isFavourite: Boolean(filterData.isFavourite),
					}
				: undefined,
		[filterData],
	);
	const [{ view }] = useSelectedViewState();
	let jiraViewResultData: typeof jiraViewData | typeof viewResultData = null;
	if (isRefactorNinToViewSchemaEnabled()) {
		if (fg('slv-37_rework_fragment_logic_in_nin_header')) {
			jiraViewResultData = jiraViewData;
		} else {
			jiraViewResultData =
				jiraViewData && isJiraSpreadsheetView(jiraViewData) ? jiraViewData : null;
		}
	} else {
		jiraViewResultData = viewResultData;
	}

	let onChangeHierarchy = noop;
	let onChangeGroupedByField = noop;
	let onChangeHideDone = noop;
	if (
		// eslint-disable-next-line jira/ff/no-preconditioning
		expVal('jira_spreadsheet_component_m1', 'isHierarchyEnabled', false) &&
		fg('jsc_m2_hierarchy_fe_changes')
	) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		({ onChangeHierarchy, onChangeGroupedByField, onChangeHideDone } = useIssueSearchActions());
	}

	let hideDone = false;
	if (fg('jsc_bulk_edit_hide_done_work_items')) {
		hideDone = isRefactorNinToViewSchemaEnabled()
			? jiraViewData?.viewSettings?.hideDone ?? false
			: viewResultData?.viewConfigSettings?.hideDone ?? false;
	}

	const HierarchyToggle =
		jiraViewResultData && view !== 'detail' ? (
			<HierarchyToggleItem
				isGroupingSupported={isGroupingSupported}
				viewResult={jiraViewResultData}
				onChangeHierarchy={onChangeHierarchy}
			/>
		) : null;

	let onResetSelectedIssues = noop;
	if (fg('jsc_list_hide_done_items')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		({ reset: onResetSelectedIssues } = useIssueTableSelectedIssuesActions());
	}

	let HideDoneToggle: JSX.Element | null = null;

	if (fg('slv-37_rework_fragment_logic_in_nin_header')) {
		HideDoneToggle =
			jiraViewResultData && view !== 'detail' && fg('jsc_list_hide_done_items') ? (
				<HideDoneItemsToggle
					viewResult={jiraViewResultData}
					onChangeHideDone={onChangeHideDone}
					onResetSelectedIssues={onResetSelectedIssues}
				/>
			) : null;
	} else {
		HideDoneToggle =
			jiraViewResultData && fg('jsc_list_hide_done_items') ? (
				<HideDoneItemsToggle
					viewResult={jiraViewResultData}
					onChangeHideDone={onChangeHideDone}
					onResetSelectedIssues={onResetSelectedIssues}
				/>
			) : null;
	}

	const GroupByDropdown =
		isGroupingSupported &&
		jiraViewData &&
		isJiraSpreadsheetView(jiraViewData) &&
		view !== 'detail' ? (
			<GroupByDropdownItem
				onChangeGroupedByField={onChangeGroupedByField}
				spreadsheetView={jiraViewData}
			/>
		) : null;

	const saveResetViewSettingsButton = jiraViewResultData &&
		expVal('saved_list_view', 'isEnabled', false) && (
			<SaveResetViewSettingsButton viewFragment={jiraViewResultData} />
		);

	if (filterQueryIsFetching) {
		return <HeaderSkeletonImage />;
	}

	return (
		<UFOLabel name="header">
			<CustomHeader
				jql={jql}
				filterId={filterId}
				filterDetails={filterDetails}
				searchResultPageData={searchResultPageData}
				HierarchyToggle={HierarchyToggle}
				GroupByDropdown={GroupByDropdown}
				HideDoneToggle={HideDoneToggle}
				SaveResetViewSettingsButton={saveResetViewSettingsButton}
				isHideDoneWorkItemsEnabled={hideDone}
			/>
		</UFOLabel>
	);
};

export default withReportErrors<HeaderProps>(Header, {
	id: 'ui.header.unhandled',
	packageName: PACKAGE_NAME,
	teamName: TEAM_NAME,
	sendToPrivacyUnsafeSplunk: true,
	attributes: isLoaderErrorAttributes,
});
